import { BaseQueryFn } from "@reduxjs/toolkit/query";
import axios, { AxiosError, AxiosHeaders, AxiosRequestConfig } from "axios";
import { CLEAR_AUTH_ACTION_TYPE } from "./constants";
import { getAccessToken } from "./authTokenProvider";

// API_BASE_URL will differ, as we use different endpoints
const factory = function (API_BASE_URL: string) {

  const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 40000,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  api.interceptors.request.use((config) => {
    const accessToken = getAccessToken();

    if (accessToken) {
      const headers = AxiosHeaders.from(config.headers);
      headers.set("Authorization", `Bearer ${accessToken}`);
      config.headers = headers;
    }

    return config;
  });

  const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig["method"];
      data?: AxiosRequestConfig["data"];
      params?: AxiosRequestConfig["params"];
      headers?: AxiosRequestConfig["headers"];
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params, headers }, baseQueryApi) => {
    const state = baseQueryApi.getState() as {
      auth?: {
        accessToken?: string;
      };
    };
    const accessToken = state.auth?.accessToken;

    try {
      const result = await api({
        url,
        method,
        data,
        params,
        headers: {
          ...headers,
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      if (err.response?.status === 401) {
        baseQueryApi.dispatch({ type: CLEAR_AUTH_ACTION_TYPE });
      }

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

  return {
    api,
    axiosBaseQuery
  }
};

export default factory
